import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapTag } from "components/icons/MapTag";
import ReactDOMServer from "react-dom/server";

interface MapPickerProps {
  onSelect: (lat: string, lng: string) => void;
  defaultCenter?: [number, number];
  center?: [number, number]; // Add this prop
}

export const MapPicker = ({ onSelect, defaultCenter = [42.0038, 21.4522], center }: MapPickerProps) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  const customIcon = L.divIcon({
    className: "custom-map-icon",
    html: ReactDOMServer.renderToStaticMarkup(<MapTag />),
    iconSize: [38, 30],
    iconAnchor: [16, 32],
  });

  // Initialize map and handle center changes
  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      // Initialize map
      mapInstance.current = L.map(mapRef.current).setView(center || defaultCenter, 13);

      // Add tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(mapInstance.current);

      // Handle map click
      mapInstance.current.on("click", (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        onSelect(lat.toFixed(6), lng.toFixed(6));

        // Add or move marker
        if (markerRef.current) {
          markerRef.current.setLatLng(e.latlng);
        } else {
          markerRef.current = L.marker(e.latlng, { icon: customIcon }).addTo(mapInstance.current!);
        }
      });

      setTimeout(() => {
        mapInstance.current?.invalidateSize();
      }, 200);
    }
  }, []);

  // Handle center prop changes
  useEffect(() => {
    if (mapInstance.current && center) {
      mapInstance.current.setView(center);
      if (markerRef.current) {
        markerRef.current.setLatLng(center);
      } else {
        markerRef.current = L.marker(center, { icon: customIcon }).addTo(mapInstance.current);
      }
    }
  }, [center]);

  return <div ref={mapRef} className="h-64 w-full rounded border" />;
};
